import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import {
  ArrowRight, Sparkles, Wand2, Plane, CalendarDays, LayoutGrid, BookOpen,
  Images, BarChart3, Users, Layers, Facebook, Instagram, Linkedin, Twitter,
  Target, Type, Image,
} from "lucide-react";

// Public /features umbrella page. Feature copy is truthful and
// traceable to real Postmoro capabilities. Email Marketing is shown
// under "Coming soon" and NOT listed as currently available.

const FEATURES = [
  { icon: Wand2, title: "AI Generate Post", body: "Draft captions, hashtags, and images in seconds. Every draft is editable before it goes live.", link: "/ai-social-media-management" },
  { icon: Plane, title: "Autopilot", body: "Postmoro plans a whole week of posts and drops them into an approval queue — or publishes automatically when you toggle it on.", link: "/features/autopilot" },
  { icon: CalendarDays, title: "Post scheduling", body: "One calendar for Facebook, Instagram, LinkedIn, and X. Drag posts to reschedule, or type a date and time.", link: "/social-media-scheduler" },
  { icon: LayoutGrid, title: "Content Manager", body: "Every draft, scheduled post, and past publication in one workspace. Filter by platform, status, or workspace." },
  { icon: BookOpen, title: "Brand Learning", body: "Paste examples of your existing posts and Postmoro adapts new drafts to match the way your business actually writes.", link: "/features/brand-learning" },
  { icon: Images, title: "Business Media Library", body: "Upload real product, staff, and storefront photos once — reuse them across future posts instead of picking through stock." },
  { icon: BarChart3, title: "Analytics", body: "Track publish activity and engagement across connected platforms so you know what's landing and what to try next." },
  { icon: Layers, title: "Multi-Business Workspaces", body: "Business subscribers can manage up to 4 separate business workspaces from a single Postmoro account, each with its own brand voice and calendar." },
  { icon: Users, title: "Teams", body: "Invite team members with Owner, Admin, or Member roles per workspace. Business plan includes 3 team seats." },
  { icon: Target, title: "Content Focus", body: "Tell Autopilot which mix of promotion, education, brand awareness, and community you want and it rotates the content categories accordingly." },
  { icon: Type, title: "Post Length controls", body: "Choose Standard, Long, or Detailed on manual generations and Autopilot so captions match the depth you want." },
  { icon: Image, title: "AI image generation", body: "Postmoro generates a matching image alongside each caption, or you can attach one from the Business Media Library." },
];

const PLATFORMS = [
  { icon: Facebook, name: "Facebook", body: "Publish to Facebook Pages you already manage." },
  { icon: Instagram, name: "Instagram", body: "Single-image feed posts to Instagram professional accounts linked to a Facebook Page." },
  { icon: Linkedin, name: "LinkedIn", body: "Publish to your LinkedIn personal profile with automatic reconnect reminders." },
  { icon: Twitter, name: "X", body: "Single posts on X — text on its own, or text with one image." },
];

export default function FeaturesPage() {
  return (
    <>
      <SEO
        title="Postmoro Features | AI Social Media Management & Automation"
        description="Explore Postmoro features for AI content creation, scheduling, Autopilot, Brand Learning, analytics, multi-business management, and social media publishing."
        path="/features"
      />
      <div className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]" data-testid="features-page">
        <MarketingHeader activePath="/features" />

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Features
            </div>
            <h1 className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]" data-testid="features-hero-title">
              Everything Postmoro does for your social media.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl">
              AI content, automated scheduling, brand-aware drafting, and
              multi-business management — all from one calm workspace.
            </p>
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 flex flex-col" data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)]">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed flex-1">{f.body}</p>
                {f.link ? (
                  <Link to={f.link} className="mt-4 inline-flex items-center gap-1 text-sm text-[color:var(--brand-primary)] hover:underline">Learn more <ArrowRight className="h-3.5 w-3.5" /></Link>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Platforms</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">The social networks Postmoro publishes to</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {PLATFORMS.map((p) => (
              <div key={p.name} className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 flex items-start gap-4" data-testid={`platform-row-${p.name.toLowerCase()}`}>
                <div className="h-10 w-10 rounded-md flex items-center justify-center bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] shrink-0">
                  <p.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold">{p.name}</h3>
                  <p className="mt-1 text-sm text-[color:var(--text-2)] leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="rounded-2xl border border-[color:rgba(255,190,110,0.35)] bg-[color:rgba(255,190,110,0.06)] p-6" data-testid="features-coming-soon">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:rgb(255,190,110)]">Coming soon</div>
            <h2 className="mt-2 font-display text-xl font-semibold">Email Marketing</h2>
            <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed max-w-2xl">
              Postmoro is bringing email marketing to Business subscribers so
              you can plan email and social from the same workspace. It's not
              live yet — we'll announce availability on the
              {" "}
              <Link to="/whats-new" className="text-[color:var(--brand-primary)] hover:underline">What's New</Link> page.
            </p>
          </div>
        </section>

        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">See Postmoro in your workspace</h2>
            <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">Start free, connect a network, generate your first post. No credit card required.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/login"><Button size="lg" className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" data-testid="features-cta-start">Start free <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              <Link to="/pricing"><Button size="lg" variant="outline" className="border-[color:var(--border-subtle)] text-[color:var(--text-1)]" data-testid="features-cta-pricing">See pricing</Button></Link>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
